import { vec, type PlayerId, type State } from '@chessshooter/shared';
import { ArenaEvents, type ArenaEvent } from './arenaFeedback';
import { ARENA_AUDIO as MIX, ARENA_SOUND_PATCHES, type SoundPatch } from './uiConstants';

type SoundName = keyof typeof ARENA_SOUND_PATCHES;

/** Cached synthesis avoids downloads and keeps the transient, body and tail in one bounded voice. */
export function synthesizeSound(patch: SoundPatch, sampleRate: number): Float32Array {
  const samples = new Float32Array(Math.ceil(patch.duration * sampleRate));
  let phase = 0, lowNoise = 0, seed = 173;
  for (let i = 0; i < samples.length; i++) {
    const t = i / sampleRate, progress = t / patch.duration;
    seed = (Math.imul(seed, 1664525) + 1013904223) | 0;
    const noise = seed / 2147483648;
    lowNoise += (noise - lowNoise) * 0.12;
    const hz = patch.startHz * Math.pow(patch.endHz / patch.startHz, progress);
    phase += 2 * Math.PI * hz / sampleRate;
    const body = Math.sin(phase) + 0.2 * Math.sin(phase * 2.01);
    const air = lowNoise * (1 - patch.grit) * 3 + noise * patch.grit;
    const envelope = Math.min(1, t / MIX.attack) * Math.exp(-patch.decay * progress)
      * Math.min(1, (patch.duration - t) / MIX.release);
    samples[i] = Math.tanh(body * patch.tone + air * patch.noise) * envelope;
  }
  return samples;
}

export class ArenaAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<SoundName, AudioBuffer>();
  private readonly voices = new Set<AudioBufferSourceNode>();
  private readonly events = new ArenaEvents();

  constructor(private enabled = true) {
    document.addEventListener('pointerdown', () => this.unlock(), true);
    document.addEventListener('keydown', () => this.unlock(), true);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); });
    window.addEventListener('pagehide', () => this.stop());
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.stop();
    else this.unlock();
  }

  unlock(): void {
    if (!this.enabled || document.hidden || typeof AudioContext === 'undefined') return;
    try {
      if (!this.context) {
        const ctx = new AudioContext();
        this.context = ctx;
        const master = ctx.createGain(), limiter = ctx.createDynamicsCompressor();
        master.gain.value = MIX.volume;
        master.connect(limiter); limiter.connect(ctx.destination);
        this.master = master;
      }
      if (this.context.state === 'suspended') void this.context.resume().catch(() => {});
    } catch { /* Optional audio must not interrupt play. */ }
  }

  reset(state?: State): void { this.stop(); this.events.reset(state); }

  update(state: State, me: PlayerId): void {
    const events = this.events.update(state);
    if (state.phase !== 'shooter' || state.winner !== null || state.transitionTicks > 0) { this.stop(); return; }
    for (const event of events) {
      if (event.kind === 'pickup' && event.ownerId !== me) continue;
      this.play(event, state, me);
    }
  }

  private play(event: ArenaEvent, state: State, me: PlayerId): void {
    const ctx = this.context;
    if (!this.enabled || document.hidden || !ctx || !this.master || ctx.state !== 'running') return;
    try {
      const name = event.kind === 'shot' ? event.weapon : event.kind;
      let buffer = this.buffers.get(name);
      if (!buffer) {
        const samples = synthesizeSound(ARENA_SOUND_PATCHES[name], ctx.sampleRate);
        buffer = ctx.createBuffer(1, samples.length, ctx.sampleRate);
        buffer.getChannelData(0).set(samples);
        this.buffers.set(name, buffer);
      }
      if (this.voices.size >= MIX.maxVoices) {
        const oldest = this.voices.values().next().value!;
        oldest.stop(); this.voices.delete(oldest);
      }
      const source = ctx.createBufferSource(), gain = ctx.createGain(), pan = ctx.createStereoPanner();
      const listener = state.players[me].pos;
      gain.gain.value = Math.max(MIX.minimumGain, 1 / (1 + vec.dist(listener, event.pos) / MIX.distance));
      pan.pan.value = Math.max(-1, Math.min(1, (event.pos.x - listener.x) / MIX.panWidth));
      source.buffer = buffer;
      source.connect(gain); gain.connect(pan); pan.connect(this.master);
      source.onended = () => { source.disconnect(); gain.disconnect(); pan.disconnect(); this.voices.delete(source); };
      this.voices.add(source);
      source.start();
    } catch { /* Playback failure must not affect the frame loop. */ }
  }

  stop(): void {
    for (const voice of this.voices) { try { voice.stop(); } catch { /* Already ended. */ } }
    this.voices.clear();
  }
}
